'use client';
import { useEffect } from 'react';
import analytics from '@/util/analytics';
import { getCkyConsentCategories } from '@/util/cookie_consent_client';
import { metaTrackingMiddleware } from '@/util/meta_tracking_client';

/**
 * Registers two Segment source middlewares: a consent gate that blocks all
 * events (track, identify, page, etc.) unless the user has granted analytics
 * consent, and Meta fbc/fbp enrichment on page events for the Conversions API.
 *
 * This approach avoids changing how analytics.ts instantiates the
 * AnalyticsBrowser — which would shift webpack module IDs and trigger
 * the Next.js 13 RSC chunk double-encoding bug on /products/[slug].
 *
 * Both are registered once on mount and check consent dynamically on every
 * event, so they respond to consent changes in real time without needing to
 * re-register.
 */
export default function SegmentInit() {
  useEffect(() => {
    analytics.addSourceMiddleware(({ payload, next }) => {
      if (getCkyConsentCategories().analytics) {
        next(payload);
      }
      // No next() call = event is silently dropped
    });

    // Registered after the consent gate so it only sees events that already
    // passed it. Dropping an event there stops the chain before this runs.
    analytics.addSourceMiddleware(metaTrackingMiddleware);

    // Expose the analytics instance on window so `privee_analytics.ts` can
    // call it without importing the module directly — importing it there
    // would shift webpack module IDs and trigger the Next.js 13 RSC chunk
    // double-encoding bug on /products/[slug] (see comment in
    // privee_analytics.ts).
    (window as unknown as { analytics?: unknown }).analytics = analytics;
  }, []);

  return null;
}
